import {
  mcGetItem,
  mcSetItemSilent,
  mcGetRoleEscalationPolicyAuthority,
  mcPatchRoleEscalationPolicy,
  mcPublishRoleEscalationPolicyIfMissing,
} from "./mcCloudSync";

export const MC_ROLE_ESCALATION_POLICY_KEY = "mc-role-escalation-policy-v1";
export const MC_ROLE_ESCALATION_EVENT = "mc-escalation-policy-change";

// "default" means every response that passed Config's Minimum response trigger.
// Severity overrides apply only to Operations and Owner, never Site Manager.
export const RESPONSE_ESCALATION_LEVELS = ["default", "low", "medium", "high", "critical"];
export function normalizeEscalationMinSeverity(value) {
  const key = String(value || "default").trim().toLowerCase();
  return RESPONSE_ESCALATION_LEVELS.includes(key) ? key : "default";
}


export const ESCALATION_ROLES = [
  { key: "siteManager", label: "Site Manager", assignmentKey: "siteManagerPersonId", defaultDelay: 0, stage: 1 },
  { key: "operationsManager", label: "Operations Manager", assignmentKey: "operationsManagerPersonId", defaultDelay: 15, stage: 2 },
  { key: "owner", label: "Owner", assignmentKey: "ownerPersonId", defaultDelay: 60, stage: 3 },
];

export function normalizeRoleEscalationPolicy(parsed = {}) {
  return ESCALATION_ROLES.reduce((result, role) => {
    const current = parsed?.[role.key] || {};
    const channels = Array.isArray(current.channels)
      ? current.channels.filter((item) => ["email", "whatsapp"].includes(item))
      : ["email", "whatsapp"];
    result[role.key] = {
      delayMinutes: Number.isFinite(Number(current.delayMinutes)) ? Math.max(0, Number(current.delayMinutes)) : role.defaultDelay,
      channels: [...new Set(channels)],
      ...(role.key !== "siteManager" ? { minSeverity: normalizeEscalationMinSeverity(current.minSeverity) } : {}),
    };
    return result;
  }, {});
}

export function loadRoleEscalationPolicy() {
  let parsed = {};
  try { parsed = JSON.parse(mcGetItem(MC_ROLE_ESCALATION_POLICY_KEY) || "{}"); } catch (_) {}
  return normalizeRoleEscalationPolicy(parsed);
}

function applyConfirmedRolePolicy(value) {
  const normalized = normalizeRoleEscalationPolicy(typeof value === "string" ? JSON.parse(value || "{}") : value);
  mcSetItemSilent(MC_ROLE_ESCALATION_POLICY_KEY, JSON.stringify(normalized));
  try { window.dispatchEvent(new CustomEvent(MC_ROLE_ESCALATION_EVENT, { detail: normalized })); } catch (_) {}
  return normalized;
}

export async function refreshRoleEscalationPolicy() {
  const source = await mcGetRoleEscalationPolicyAuthority();
  if (!source.exists) return { ...source, policy: null };
  return { ...source, policy: applyConfirmedRolePolicy(source.value) };
}

export async function updateRoleEscalationPolicy(roleKey, patch, expectedOrgId) {
  const saved = await mcPatchRoleEscalationPolicy(roleKey, patch, expectedOrgId);
  return { ...saved, policy: applyConfirmedRolePolicy(saved.value) };
}

export async function saveRoleEscalationPolicy(policy = {}, expectedOrgId) {
  // Used only to initialize a missing policy. Regular edits MUST use the
  // server-merged role patch above, never a stale whole-policy write.
  const saved = await mcPublishRoleEscalationPolicyIfMissing(policy, expectedOrgId);
  return { ...saved, policy: applyConfirmedRolePolicy(saved.value) };
}

export function buildSiteEscalationPlan(site = {}, profiles = [], policy = loadRoleEscalationPolicy()) {
  const byId = new Map((profiles || []).map((profile) => [profile.id, profile]));
  return ESCALATION_ROLES.map((role) => {
    const personId = site?.[role.assignmentKey] || "";
    const person = personId ? byId.get(personId) || null : null;
    return {
      stage: role.stage,
      roleKey: role.key,
      roleLabel: role.label,
      personId,
      person,
      delayMinutes: policy?.[role.key]?.delayMinutes ?? role.defaultDelay,
      channels: policy?.[role.key]?.channels || ["email", "whatsapp"],
      ...(role.key !== "siteManager" ? { minSeverity: normalizeEscalationMinSeverity(policy?.[role.key]?.minSeverity) } : {}),
    };
  });
}

// Delays are measured from completion, exactly as shown in People > Escalation.
// Keep all roles in the snapshot: an unassigned role must never shift the next
// role's timing or turn Operations into the initial Site Manager recipient.
export function buildResponseRoleEscalationPlan(site = {}, createdAt, policy = loadRoleEscalationPolicy()) {
  const normalized = normalizeRoleEscalationPolicy(policy);
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) throw new Error("A completed count timestamp is required.");
  // Freeze severity gates in the response just like timing and channels; the
  // backend enforces them before creating Cloud Tasks for each new response.
  const stages = ESCALATION_ROLES.map((role) => ({
    stage: role.stage,
    roleKey: role.key,
    roleLabel: role.label,
    personId: site?.[role.assignmentKey] || "",
    ...normalized[role.key],
    dueAt: new Date(created + normalized[role.key].delayMinutes * 60000).toISOString(),
  }));
  return {
    version: 2,
    policySource: MC_ROLE_ESCALATION_POLICY_KEY,
    stage: "site_manager",
    stages,
    siteManagerAt: stages[0].dueAt,
    operationsManagerAt: stages[1].dueAt,
    ownerAt: stages[2].dueAt,
    operationsAfterMinutes: stages[1].delayMinutes,
    ownerAfterMinutes: stages[2].delayMinutes,
    highestNotified: "",
    complete: false,
  };
}
